// The product tiles a cashier taps.
//
// Large targets, the price prominent, the unit stated plainly, and stock shown
// with an icon and a word rather than colour alone. A product that is out of
// stock is still visible but cannot be added - hiding it would leave the
// cashier wondering where it went.
//
// SIX COLUMNS on a standard desktop, and the ladder below it, because the
// number of cards across is a decision about how far a cashier's hand travels -
// not something to leave to whatever `auto-fill` happens to produce.
//
// The ladder is split by the `till` breakpoint, because the same viewport width
// means two different product-area widths depending on whether the Current
// Order has a column. A 1093 px laptop in drawer mode has MORE room for
// products than a 1152 px one with the order panel open, so the two are keyed
// separately rather than made to share one sequence:
//
//   drawer mode (< 1100)      2 -> 3 -> 4        phone, tablet, small laptop
//   with the order column     4 -> 5 -> 6        1100+, 1400+, 1700+
//
// The 1400 step is the same number AppLayout uses to decide the menu is no
// longer a rail: below it the product area is 600-790 px, and a fifth column
// there makes each card narrower than the price printed on it.
//
// Every track is `minmax(0, 1fr)`. That zero is what lets a long Arabic product
// name wrap inside its card instead of widening the column - and with it the
// page - which is the whole reason the grid is written this way.

import { AlertTriangle, Flame, ImageOff, PackageX } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { formatLbp, formatUsd, toDecimal, unitLabel } from '../../utils/format';
import { quotedUnitPrice, unitPriceInLbp } from '../../stores/cartStore';
import { optionLabelText } from '../../utils/optionLabel';
import { SkeletonCards } from '../common/StateBlocks';
import { mediaSrcSet } from '../../api/media.api';
import type { ComboOffer, Product } from '../../types';

interface ProductGridProps {
  products: Product[];
  loading: boolean;
  onSelect: (product: Product) => void;
  /// The combos shown on this shelf - only the virtual «العروض» tab passes
  /// any. A combo card goes straight into the cart: its composition is
  /// fixed, so there is no quantity or option to ask for.
  combos?: ComboOffer[];
  onSelectCombo?: (combo: ComboOffer) => void;
  /// The shop's current USD to LBP rate, from the same query the header and
  /// the payment dialog read. Null until one has been set, which is a normal
  /// state on a new install rather than an error.
  ///
  /// Passed in rather than fetched here so there is ONE rate in the POS: when
  /// the owner changes it in Settings the query is invalidated, this re-renders
  /// with everything else, and no card can be showing yesterday's number while
  /// the cart shows today's.
  exchangeRate?: string | null;
}

export function ProductGrid({
  products,
  loading,
  onSelect,
  exchangeRate,
  combos = [],
  onSelectCombo,
}: ProductGridProps) {
  const { t, locale, format } = useI18n();

  // A rate of zero or nonsense is treated as no rate at all: multiplying by it
  // would put `0 LBP` on every tile, which reads as "free" rather than as
  // "not configured".
  const rate = exchangeRate && toDecimal(exchangeRate).greaterThan(0) ? exchangeRate : null;

  if (loading) {
    return (
      <div className="p-1">
        <SkeletonCards count={8} />
      </div>
    );
  }

  return (
    <div
      // The column count comes from the PRODUCT PANEL's own width, not the
      // window's. They are different numbers and the difference is the cart:
      // opening it takes 22rem off this panel without the window changing at
      // all, so a window-width breakpoint puts six cards in a space that fits
      // four. See `.product-grid` in index.css - it is a container query,
      // and it stops at five.
      className="product-grid"
    >
      {/* --- combos first: a bundle at one price ------------------------ */}
      {combos.map((combo) => {
        const comboName = locale === 'ar' ? combo.nameAr : combo.nameEn;
        return (
          <button
            key={`combo-${combo.id}`}
            type="button"
            disabled={!combo.available}
            onClick={() => onSelectCombo?.(combo)}
            aria-label={`${t.offers.comboBadge}: ${comboName}`}
            className={clsx(
              'group flex min-h-touch-lg min-w-0 flex-col overflow-hidden rounded-tile border text-start transition-all duration-150',
              combo.available
                ? 'border-line bg-surface shadow-card hover:-translate-y-0.5 hover:border-butcher-green hover:shadow-raised active:translate-y-0'
                : 'cursor-not-allowed border-line bg-surface-sunken opacity-60',
            )}
          >
            <div className="relative flex aspect-[5/4] w-full shrink-0 items-center justify-center overflow-hidden bg-surface-sunken">
              {combo.imageUrl ? (
                <img src={combo.imageUrl} srcSet={mediaSrcSet(combo.imageUrl)} sizes="(max-width: 640px) 45vw, 220px" alt="" loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <Flame className="h-1/3 max-h-7 w-1/3 max-w-7 text-burgundy-red/60" aria-hidden="true" />
              )}
              <span className="absolute start-1.5 top-1.5 flex items-center gap-1 rounded-full bg-burgundy-red px-2 py-0.5 text-[10px] font-semibold text-white">
                <Flame className="h-3 w-3" aria-hidden="true" />
                {t.offers.comboBadge}
              </span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
              <p className="line-clamp-2 min-h-[2.5rem] break-words text-sm font-semibold leading-snug text-ink">
                {comboName}
              </p>
              {/* The price column is one left-anchored block in either
                  language: the struck regular total, the offer price and the
                  dollars all start at the same left edge. */}
              <div className="mt-2 flex min-w-0 flex-col items-start" dir="ltr">
                <span className="numeric block text-xs text-ink-muted line-through">
                  {formatLbp(combo.regularTotalLbp, locale)}
                </span>
                <span className="numeric product-price-primary block font-bold text-butcher-green">
                  {formatLbp(combo.offerPriceLbp, locale)}
                </span>
              </div>
            </div>
          </button>
        );
      })}

      {products.map((product) => {
        const outOfStock = product.stockStatus === 'OUT_OF_STOCK';
        const low = product.stockStatus === 'LOW';
        const name = locale === 'ar' ? product.nameAr : product.nameEn;
        // THE PRICE THE SHOP SET, when it set one. This used to convert the
        // dollar column unconditionally, so a product priced at exactly
        // 1,400,000 advertised 1,400,004 on the busiest screen in the shop -
        // and the modal behind it said something else again.
        // THE EFFECTIVE PRICE when the server sent one - the base through
        // the product's or the shelf's rule, worked out by the one engine -
        // and the base beside it, struck through. Without a promotion block
        // the card prices from the base exactly as it always did.
        const promotion = product.promotion && product.promotion.promotionType !== 'NONE' ? product.promotion : null;
        const lbpPrice = rate
          ? formatLbp(
              // THE ONE QUOTE - the same figure the weight panel and the cart use.
              unitPriceInLbp(quotedUnitPrice(product, null).unitPriceLbp, product.sellingPrice, rate),
              locale,
            )
          : null;
        const basePrice = promotion && rate ? formatLbp(promotion.basePriceLbp, locale) : null;
        // An option product wears the badge when ANY of its options is
        // discounted - the card cannot show a price, but it can say so.
        const optionDiscount = product.hasVariants
          ? (product.variants ?? []).find((v) => v.isActive && v.promotion && v.promotion.promotionType !== 'NONE')?.promotion ?? null
          : null;
        const badgePercent = (promotion ?? optionDiscount)?.discountPercent ?? null;

        // A product sold as types shows HOW MANY, not a price. The parent has
        // no sale price - the type chosen at the next tap does - and printing
        // the parent's compatibility figure here would read as "this is what
        // it costs", which is the one thing it is not.
        const offered = product.hasVariants ? (product.variants ?? []).filter((v) => v.isActive) : [];
        const isVariantProduct = offered.length > 0;

        return (
          <button
            key={product.id}
            type="button"
            disabled={outOfStock}
            onClick={() => onSelect(product)}
            className={clsx(
              'group flex min-h-touch-lg min-w-0 flex-col overflow-hidden rounded-tile border text-start transition-all duration-150',
              outOfStock
                ? 'cursor-not-allowed border-line bg-surface-sunken opacity-60'
                : 'border-line bg-surface shadow-card hover:-translate-y-0.5 hover:border-butcher-green hover:shadow-raised active:translate-y-0',
            )}
          >
            {/* --- image ------------------------------------------------- */}
            {/* 5:4 rather than 4:3 - the same width buys a little more height,
                so the meat is easier to recognise at a glance without the card
                growing tall enough to lose a row off the bottom. */}
            <div className="relative flex aspect-[5/4] w-full shrink-0 items-center justify-center overflow-hidden bg-surface-sunken">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  // A till tile is about 200 pixels wide; the stored original
                  // is a 2.3 MB photograph at 1450. These let the browser take
                  // the copy that fits, which is what makes a full grid of
                  // products load at once rather than in slow dribs.
                  srcSet={mediaSrcSet(product.imageUrl)}
                  sizes="(max-width: 640px) 45vw, 220px"
                  alt=""
                  loading="lazy"
                  // `cover`, not `contain`: a product photo cropped to fill the
                  // frame reads as a photograph of meat. `contain` would letter-
                  // box it against grey and make a row of cards look broken.
                  className="h-full w-full object-cover"
                />
              ) : (
                // Sized in relative units so the placeholder shrinks with the
                // tile instead of overflowing a narrow column.
                <ImageOff className="h-1/3 max-h-7 w-1/3 max-w-7 text-ink-muted/40" aria-hidden="true" />
              )}

              {/* The discount, compact, in the image corner. */}
              {badgePercent ? (
                <span className="absolute start-1.5 top-1.5 rounded-full bg-burgundy-red px-2 py-0.5 text-[10px] font-semibold text-white">
                  {format(t.pricing.discountBadge, { percent: toDecimal(badgePercent).toFixed(0) })}
                </span>
              ) : null}

              {/* Status is words plus an icon, never colour on its own. */}
              {outOfStock ? (
                <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-burgundy-red/90 py-1 text-[11px] font-semibold text-white">
                  <PackageX className="h-3 w-3" aria-hidden="true" />
                  {t.pos.outOfStock}
                </span>
              ) : low ? (
                <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-antique-gold/90 py-1 text-[11px] font-semibold text-charcoal">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  {t.pos.lowStock}
                </span>
              ) : null}
            </div>

            {/* --- name and price ---------------------------------------- */}
            <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
              {/* Two lines ALWAYS: `line-clamp-2` caps a long name and the
                  matching `min-h` reserves the second line for a short one.
                  Without the reserve, "Sausages" makes a card shorter than
                  "Chicken Tawook" beside it and the price row zig-zags across
                  the grid.

                  `break-words` handles the other end: a 40-character name with
                  no spaces would be one unbreakable box wider than its column,
                  and the page would scroll sideways because of it. */}
              <p className="line-clamp-2 min-h-[2.5rem] break-words text-sm font-semibold leading-snug text-ink">
                {name}
              </p>

              {/* --- price ---------------------------------------------------
                  POUNDS FIRST, and that is not a style choice. The shop prices
                  in dollars and is paid in lira; the number a cashier reads
                  aloud, and the number the customer is holding, is the lira
                  one. Leading with dollars made every card a conversion the
                  cashier had to do in their head.

                  The dollar figure stays, smaller, because it is what the
                  price was SET in - it is the one that does not move when the
                  rate does, and the owner still needs to see it.

                TWO LINES, ALWAYS THE SAME TWO. The lira figure has the first
                line to itself, so `22,822,500 LBP` never has to share 168px
                with anything; the dollars and the unit share the second, where
                they read as one quiet phrase - "$12.00 per kg" - rather than
                as two more facts competing with the big number above. */}
              {isVariantProduct ? (
                // Same two lines the price occupies, so a shelf of mixed
                // cards keeps one baseline instead of zig-zagging.
                <div className="mt-2 min-w-0">
                  <span className="product-price-primary block font-bold text-butcher-green">
                    {offered.length === 1
                      ? t.products.variantCount_one
                      : format(t.products.variantCount_other, { count: offered.length })}
                  </span>
                  {/* In this product's own words: "اختر الحجم" on the chips,
                      "اختر النوع" on the tawook. The card is where the cashier
                      decides what to tap, so the question they are about to be
                      asked belongs on it. */}
                  <span className="mt-0.5 block text-xs text-ink-muted">
                    {format(t.products.chooseOption, {
                      label: optionLabelText(
                        { ar: product.optionLabelAr, en: product.optionLabelEn },
                        locale,
                      ),
                    })}
                  </span>
                </div>
              ) : (
              <div className="mt-2 flex min-w-0 flex-col items-start" dir="ltr">
                {/* One left-anchored price column in either language: the
                    struck base, the price row and the dollars share a left
                    edge, each line a left-to-right run. */}
                {basePrice ? (
                  <span className="numeric block text-xs text-ink-muted line-through">{basePrice}</span>
                ) : null}
                {/* THE PRICE LINE, read left to right in either language:
                    the figure, then the currency it is in, then what it is
                    for - «1,700,000 LBP لكل كغ». The row is LTR by
                    structure, so the page's own direction never turns the
                    unit around to the front. A unit the tile cannot fit
                    beside the figure wraps under it, still after it, at the
                    same left edge as the rest of the column. */}
                <span className="product-price-primary flex max-w-full min-w-0 flex-wrap items-baseline gap-x-1.5 font-bold text-butcher-green" dir="ltr">
                  <span className="numeric">
                    {lbpPrice ?? formatUsd(product.sellingPrice)}
                  </span>
                  <span className="text-xs font-medium text-ink-muted" data-testid="tile-unit">
                    {format(t.products.perUnit, { unit: unitLabel(product.sellingUnit, locale) })}
                  </span>
                </span>

                {/* The dollars, on their own line under the lira. Omitted when
                    there is no rate: the line above is already the dollar
                    price, and printing it twice would look like two different
                    numbers. */}
                {lbpPrice ? (
                  <span className="numeric mt-0.5 block text-xs font-medium text-ink-muted">
                    {promotion && rate
                      ? formatUsd(toDecimal(promotion.effectivePriceLbp).dividedBy(toDecimal(rate)).toDecimalPlaces(2))
                      : formatUsd(product.sellingPrice)}
                  </span>
                ) : null}
              </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
